import { useEffect, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import type { Breakpoint } from '@mui/material/styles';

interface DialogModalProps {
  isOpen: boolean;
  modalTitle: ReactNode;
  modalContent: ReactNode;
  maxWidth?: Breakpoint;
  titleClassName?: string;
  showCloseIcon?: boolean;
  onClose: () => void;
  modalCancelButtonText?: string;
  modalCancelButtonDisabled?: boolean;
  onCancel?: () => void;
  modalOkButtonText?: string;
  modalOkButtonDisabled?: boolean;
  showOkButtonLoader?: boolean;
  onOk?: () => void;
}

/**
 * Shared modal shell. Escape and the close icon both route through `onClose`;
 * the footer buttons are only rendered when their label prop is supplied.
 */
const DialogModal = ({
  isOpen,
  modalTitle,
  modalContent,
  maxWidth = 'xs',
  titleClassName,
  showCloseIcon = false,
  onClose,
  modalCancelButtonText,
  modalCancelButtonDisabled = false,
  onCancel,
  modalOkButtonText,
  modalOkButtonDisabled = false,
  showOkButtonLoader = false,
  onOk,
}: DialogModalProps) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keyup', handleEscapeKey);
    return () => window.removeEventListener('keyup', handleEscapeKey);
  }, [onClose]);

  const hasFooter = Boolean(modalCancelButtonText || modalOkButtonText);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      fullWidth
      maxWidth={maxWidth}
      className='jupiter-dialog-model'
    >
      <DialogTitle id='alert-dialog-title' className={titleClassName}>
        {modalTitle}
        {showCloseIcon ? (
          <IconButton
            aria-label='Close'
            className='close-icon'
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>

      <DialogContent className='alert-dialog-content_wrapper'>
        <div id='alert-dialog-description'>{modalContent}</div>
      </DialogContent>

      {hasFooter ? (
        <DialogActions className='alert-dialog-footer_wrapper'>
          {modalCancelButtonText ? (
            <Button
              variant='outlined'
              color='primary'
              onClick={onCancel ?? onClose}
              disabled={modalCancelButtonDisabled}
            >
              {modalCancelButtonText}
            </Button>
          ) : null}

          {modalOkButtonText ? (
            <Button
              variant='contained'
              color='primary'
              onClick={onOk}
              disabled={modalOkButtonDisabled}
            >
              {modalOkButtonText}
              {showOkButtonLoader ? (
                <Box className='CustLodder' padding={1}>
                  <CircularProgress size={20} />
                </Box>
              ) : null}
            </Button>
          ) : null}
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default DialogModal;
